import { atom as f, map as m, computed as y } from "nanostores";
import { copilotTools as b, copilotInstructions as C, copilotContext as F, uniqueId as w } from "./global-state.js";
const S = {
  idle: ["initializing", "ending"],
  initializing: ["ready", "error"],
  ready: ["sending", "ending"],
  sending: ["receiving"],
  receiving: ["ready"],
  ending: ["ended", "error"],
  error: ["initializing", "ending"],
  ended: []
  // Can only transition to itself (terminal state)
};
function x(o, e) {
  if (o === e)
    return o;
  if (!S[o])
    throw new Error(`Unknown status: ${o}`);
  if (S[o].includes(e))
    return e;
  throw new Error(`Invalid status transition: ${o} -> ${e}`);
}
class D {
  constructor(e) {
    this.config = {
      includeGlobalContext: !0,
      includeGlobalTools: !0,
      includeGlobalInstructions: !0
    }, this.__status = f("idle"), this.__messages = f([]), this.__meta = f({}), this.__uploadFiles = f([]), this.__serverSideTools = m({}), this.__sessionInstructions = m({}), this.__sessionTools = m({}), this.__sessionContext = m({}), this.__tools = y(
      [this.__serverSideTools, b, this.__sessionTools],
      (t, i, n) => {
        const l = {}, s = {
          ...this.config.includeGlobalTools === !0 ? i : {},
          ...n
        };
        for (const r of Object.values(t))
          if (r != null) {
            const h = s[r.name];
            l[r.name] = {
              ...h?.type === "server" ? h : void 0,
              ...r
            };
          }
        for (const r of Object.values(s))
          r != null && r.type === "client" && l[r.name] == null && (l[r.name] = r);
        return l;
      }
    ), this.__statefulMessages = y([this.__messages, this.__tools], E), this.__instructions = y(
      [C, this.__sessionInstructions],
      (t, i) => ({
        ...this.config.includeGlobalInstructions === !0 ? t : {},
        ...i
      })
    ), this.__context = y([F, this.__sessionContext], (t, i) => ({
      ...this.config.includeGlobalContext === !0 ? t : {},
      ...i
    })), this.__suggestionsRefreshTrigger = f([]), e && (this.config = { ...this.config, ...e });
  }
  /**
   * The current status of the Copilot session.
   */
  get status() {
    return this.__status.get();
  }
  /**
   * If the session is being ended.
   */
  get isEnding() {
    return this.status === "ending";
  }
  get isEnded() {
    return this.status === "ended";
  }
  /**
   * Sets the status of the Copilot session.
   */
  setStatus(e) {
    this.__status.set(x(this.status, e));
  }
  /**
   * adds an instruction just for this session
   */
  addInstruction(e) {
    const t = w();
    return this.__sessionInstructions.setKey(t, e), () => {
      this.__sessionInstructions.setKey(t, void 0);
    };
  }
  /**
   * Adds a tool just for this session
   */
  addTool(e) {
    return this.__sessionTools.setKey(e.name, e), () => {
      this.__sessionTools.setKey(e.name, void 0);
    };
  }
  /**
   * Adds a context just for this session
   */
  addContext(e) {
    const t = w();
    return this.__sessionContext.setKey(t, e), () => {
      this.__sessionContext.setKey(t, void 0);
    };
  }
  /**
   * Sets the meta data for this session
   */
  setSessionMeta(e) {
    this.__meta.set(e);
  }
  /**
   * Gets the meta data for this session
   */
  getSessionMeta() {
    return this.__meta.get();
  }
  /**
   * Updates the meta data for a specific message by targeting message.meta.id
   */
  setMessageMeta(e, t) {
    const n = this.__messages.get().map((l) => l.role === "model" && l.type === "meta" && "meta" in l && l.meta?.id === e ? {
      ...l,
      meta: t
    } : l);
    this.__messages.set(n);
  }
  /**
   * Forces a refresh of suggestions for this session.
   * This will trigger any active suggestion hooks to regenerate their suggestions.
   * @param instruction - Custom instructions to include in the next suggestions fetch.
   * These instructions will be used once and then cleared.
   */
  refreshSuggestions(e) {
    this.__suggestionsRefreshTrigger.set([{ instruction: e }]);
  }
  /**
   * Initializes a new session, either from an existing session id,
   * or by creating a new one
   */
  async initialize(e) {
    if (this.status !== "idle" && this.status !== "error")
      return this.id;
    this.setStatus("initializing");
    try {
      const t = window.__hydra_copilot__?.startSession;
      if (t == null)
        throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
      if (e == null) {
        const i = await t();
        if (i.id == null)
          throw new Error("Failed to start session, no session ID returned");
        this.id = i.id, this.__serverSideTools.set(T(i.tools ?? []));
      } else {
        this.id = e;
        const i = await this.loadSession();
        this.__messages.set(i.messages ?? []), this.__meta.set(i.meta ?? {}), this.__serverSideTools.set(T(i.tools ?? []));
      }
      return this.setStatus("ready"), this.id;
    } catch (t) {
      throw this.setStatus("error"), t;
    }
  }
  /**
   * Ends the current session, clearing all messages and state.
   */
  async end() {
    this.setStatus("ending");
    try {
      const e = window.__hydra_copilot__?.endSession;
      if (e == null)
        throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
      this.id != null && await e(this.id), this.__messages.set([]), this.id = void 0, this.__sessionInstructions.set({}), this.__sessionTools.set({}), this.__sessionContext.set({}), this.__serverSideTools.set({}), this.__meta.set({}), this.setStatus("ended");
    } catch (e) {
      throw this.setStatus("error"), e;
    }
  }
  async sendMessage(e) {
    const t = typeof e == "string" ? [{ role: "user", type: "text", text: e, timestamp: Date.now() }] : e, i = window.__hydra_copilot__?.sendStreamingMessage;
    if (i == null)
      throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
    if (this.status === "idle" && await this.initialize(), this.status !== "ready")
      throw new Error(`Cannot send message while session is in "${this.status}" state.`);
    try {
      this.setStatus("sending");
      let n = t.map((a) => ({
        ...a,
        timestamp: a.timestamp || Date.now()
      })), l = 0;
      for (; n.length > 0; ) {
        this.__messages.set([...this.__messages.get(), ...n]);
        const a = [], s = (d) => {
          if (this.setStatus("receiving"), l += 1, this.__messages.set([...this.__messages.get(), d]), d.role === "model" && d.type == "tool_call")
            for (const u of d.tool_calls) {
              if (u.is_client_tool !== !0)
                continue;
              const _ = this.__tools.get()[u.name];
              a.push(
                (async () => {
                  try {
                    if (_?.type === "server")
                      throw new Error(
                        `Tool ${u.name} is a server-side tool and cannot be called on the client.`
                      );
                    if (_?.handler == null)
                      throw new Error(`Tool ${u.name} does not have a handler defined.`);
                    const c = await _.handler({ args: u.args, session: this, toolCallId: u.id }) ?? null;
                    return {
                      tool_call_id: u.id,
                      name: u.name,
                      response: { result: c, error: null },
                      finished: !0
                    };
                  } catch (c) {
                    return {
                      tool_call_id: u.id,
                      name: u.name,
                      response: {
                        result: null,
                        error: `Error calling tool ${u.name}: ${c instanceof Error ? c.message : String(c)}`
                      },
                      finished: !0
                    };
                  }
                })()
              );
            }
          if (d.role === "user" && d.type === "tool_response")
            for (const u of d.tool_responses) {
              const _ = this.__tools.get()[u.name], c = j(
                [...this.__messages.get(), ...n],
                u.tool_call_id
              );
              _?.type === "server" && _?.handler != null && u.finished === !0 && _.handler({
                args: c?.args || {},
                session: this,
                response: u.response,
                toolCallId: u.tool_call_id
              });
            }
        }, r = {
          id: this.id,
          messages: n,
          tools: I(this.__tools.get()),
          instructions: M(this.__instructions.get()),
          context: U(this.__context.get()),
          onMessage: s
        };
        if (await i(r), a.length === 0) {
          n = [];
          continue;
        }
        n = [
          {
            role: "user",
            type: "tool_response",
            tool_responses: await Promise.all(a),
            timestamp: Date.now()
          }
        ];
      }
      if (l === 0)
        throw new Error("No messages received from the server. Please check your connection.");
      this.setStatus("ready");
    } catch (n) {
      throw this.setStatus("receiving"), this.setStatus("ready"), n;
    }
  }
  /**
   * Loads the session data (messages and meta)
   */
  async loadSession() {
    const e = window.__hydra_copilot__?.getSession;
    if (e == null)
      throw new Error("Copilot session not configured. Please set the Copilot configuration first.");
    if (this.id == null)
      throw new Error("Session not started");
    return await e(this.id);
  }
  /**
   * Waits for the session to be ready
   */
  async waitUntilReady() {
    if (this.status !== "ready") {
      if (this.status === "idle") {
        await this.initialize();
        return;
      }
      if (this.status === "initializing")
        return new Promise((e, t) => {
          let i = null, n = null;
          const l = () => {
            i !== null && (clearTimeout(i), i = null), n !== null && (n(), n = null);
          };
          n = this.__status.subscribe((a) => {
            a === "ready" ? (l(), e()) : a === "error" ? (l(), t(new Error("Session initialization failed"))) : a !== "initializing" && (l(), t(new Error(`Unexpected session status: ${a}`)));
          }), i = setTimeout(() => {
            l(), t(new Error("Session initialization timeout - exceeded 30 seconds"));
          }, 3e4);
        });
      throw this.status === "error" ? new Error("Session is in error state") : new Error(`Cannot wait for session in "${this.status}" state`);
    }
  }
  /**
   * Gets upload signature for file upload
   * Note: Session readiness is now checked at the upload level (uploadCopilotFile)
   */
  async getUploadSignature(e) {
    if (!this.id)
      throw new Error("Session ID not available - ensure session is ready before calling getUploadSignature");
    const t = window.__hydra_copilot__;
    if (!t?.getUploadSignature)
      throw new Error("File upload not configured. Please provide getUploadSignature in setCopilotConfig.");
    return await t.getUploadSignature({
      id: this.id,
      size: e.size,
      mime_type: e.mime_type
    });
  }
  /**
   * Gets download URL for a previously uploaded file
   */
  async getFileDownloadUrl(e) {
    if (!this.id)
      throw new Error("Session ID not available - ensure session is ready before calling getFileDownloadUrl");
    const t = window.__hydra_copilot__;
    if (!t?.getFileDownloadUrl)
      throw new Error("File download not configured. Please provide getFileDownloadUrl in setCopilotConfig.");
    return (await t.getFileDownloadUrl({
      id: this.id,
      filename: e
    })).url;
  }
  /**
   * Add files to the upload queue
   */
  addUploadFiles(e) {
    const t = this.__uploadFiles.get();
    this.__uploadFiles.set([...t, ...e]);
  }
  /**
   * Update a specific upload file
   */
  updateUploadFile(e, t) {
    const n = this.__uploadFiles.get().map((l) => l.id === e ? { ...l, ...t } : l);
    this.__uploadFiles.set(n);
  }
  /**
   * Remove upload file
   */
  removeUploadFile(e) {
    const i = this.__uploadFiles.get().filter((n) => n.id !== e);
    this.__uploadFiles.set(i);
  }
  /**
   * Clear all upload files
   */
  clearUploadFiles() {
    this.__uploadFiles.set([]);
  }
}
function v(o) {
  return typeof o == "string" ? o : typeof o == "number" || typeof o == "boolean" ? String(o) : Array.isArray(o) || typeof o == "object" ? JSON.stringify(o) : String(o);
}
function E(o, e) {
  const t = [];
  let i = [];
  const n = {}, l = o.filter(
    (s) => {
      if (s.role === "user" && s.type === "tool_response") {
        for (const r of s.tool_responses)
          r.tool_call_id != null && (n[r.tool_call_id] == null && (n[r.tool_call_id] = []), n[r.tool_call_id].push(r));
        return !1;
      }
      return s.type === "text" || s.type === "content" || s.type === "tool_call" || s.type == "meta" || s.type === "error";
    }
  );
  function a() {
    const s = i.filter((d) => d.type === "text"), r = i.find((d) => d.type === "meta" && "meta" in d);
    if (i = [], s.length === 0)
      return;
    const h = s.map((d) => d.text).join(""), p = r?.meta ?? {};
    t.push({
      role: "model",
      type: "text",
      text: h,
      timestamp: s[0].timestamp,
      meta: p
    }), i = [];
  }
  for (const s of l)
    s.role === "model" && s.type === "text" ? i.push(s) : s.role === "user" && (s.type === "text" || s.type === "content") ? (a(), t.push(s)) : s.role === "model" && s.type === "meta" ? (i.push(s), a()) : s.role === "model" && s.type === "tool_call" ? (a(), t.push({
      role: "model",
      type: "tool_call",
      tool_calls: s.tool_calls.map((r) => {
        const h = n[r.id], p = e[r.name];
        if (h == null || h.length === 0)
          return {
            ...r,
            status: "executing"
          };
        const d = h.find((c) => c.response.error != null && c.response.error !== ""), u = h.some((c) => c.finished === !0);
        if (d != null)
          return {
            ...r,
            status: "error",
            response: d.response
          };
        const _ = p?.type === "server" && p?.isStreaming === !0;
        return u && h.length === 1 && !_ ? {
          ...r,
          status: "success",
          response: h[0].response
        } : {
          ...r,
          status: u ? "success" : "executing",
          response: {
            result: h.map((c) => c.response.result),
            error: null
          }
        };
      }),
      timestamp: s.timestamp
    })) : s.role === "model" && s.type === "error" ? (a(), t.push(s)) : console.error("Unknown message type", s);
  return a(), t;
}
function I(o) {
  return Object.values(o).filter((e) => e !== void 0 && e.type === "client").map((e) => ({
    name: e.name,
    description: typeof e.description == "string" ? g(e.description) : e.description,
    request_json_schema: e.requestSchema,
    json_response_schema: e.responseSchema
  }));
}
function M(o) {
  return Array.isArray(o) ? o.map((e) => g(e.instruction).trim()) : Object.values(o).filter((e) => e !== void 0).map((e) => g(e.instruction).trim());
}
function U(o) {
  return Array.isArray(o) ? o.map((e) => ({
    description: g(e.description),
    value: v(typeof e.value == "function" ? e.value() : e.value)
  })) : Object.values(o).filter((e) => e !== void 0).map((e) => ({
    description: g(e.description),
    value: v(typeof e.value == "function" ? e.value() : e.value)
  }));
}
function g(o) {
  const e = o.split(`
`), t = e.filter((a) => a.trim().length > 0);
  if (t.length === 0)
    return o;
  const i = t.map((a) => {
    const s = a.match(/^(\s*)/);
    return s ? s[1].length : 0;
  }), n = Math.min(...i);
  return e.map((a) => a.trim().length === 0 ? a : a.slice(n)).join(`
`);
}
function j(o, e) {
  for (const t of o)
    if (t.role === "model" && t.type === "tool_call") {
      const i = t.tool_calls.find((n) => n.id === e);
      if (i)
        return i;
    }
}
function T(o) {
  const e = {};
  for (const t of o)
    e[t.name] = {
      type: "server",
      name: t.name,
      description: t.description,
      requestSchema: t.request_json_schema,
      responseSchema: t.response_json_schema
    };
  return e;
}
export {
  D as CopilotSession,
  j as findToolCallById,
  U as prepareContextForTransport,
  M as prepareInstructionsForTransport,
  I as prepareToolsForTransport,
  T as serverSideToolsToCopilotTools,
  v as stringifyForCopilot,
  g as undent,
  x as updatedStatus
};
